import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef, inject, Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  GuardResult,
  MaybeAsync,
  RouterStateSnapshot
} from '@angular/router';
import { Apollo, gql } from 'apollo-angular';
import { map } from 'rxjs';
import { AuthorizationService } from '~ngx-shared/authentication';
import { QueryUtil } from '~ngx-shared/utils';
import { CorePersonDataModel } from '~shared/models';
import { AuthorizationUtil, RoleEnum } from '~shared/utils';

@Injectable({ providedIn: 'root' })
export class CanAccessPersonDetailGuard implements CanActivate {
  private readonly destroyRef = inject(DestroyRef);
  private readonly apollo = inject(Apollo);
  private readonly authorizationService = inject(AuthorizationService);

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): MaybeAsync<GuardResult> {
    const personId = route.paramMap.get('person-id');
    if (!personId) {
      return false;
    }
    if (this.authorizationService.can(AuthorizationUtil.GROUP_EDUCATION)) {
      return true;
    }
    if (
      this.authorizationService.can(RoleEnum.INSPECTOR) ||
      this.authorizationService.can(RoleEnum.SCHOOL_OFFICE)
    ) {
      return this.apollo
        .query<{ result: CorePersonDataModel[] }>({
          query: gql`
            query ReadCoreCurrentPersonDataById($id: bigint!) {
              result: core_current_person_data(
                where: {
                  person_id: { _eq: $id }
                  ${this.authorizationService.can(RoleEnum.INSPECTOR) ? '_or: [{can_sign: {_eq: true}}, {active_latest_school_office_data: {}}, {active_latest_education_office_data: {}} ]' : ''}
                  ${this.authorizationService.can(RoleEnum.SCHOOL_OFFICE) ? '_or: [{can_sign: {_eq: true}}, {active_latest_education_office_data: {}} ]' : ''}
                }
                limit: 1
              ) {
                id
              }
            }
          `,
          variables: {
            id: personId
          }
        })
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          map(queryResult => {
            // If the result is empty, then the user has access to the person
            if (!QueryUtil.dataOrThrow(queryResult).result.length) {
              return true;
            }
            // If the result is not empty, then the user does not have access to the person
            return false;
          })
        );
    }

    return false;
  }
}
